/**
 * Per assets/handoffs/design_handoff_sidebar_alerts. A persistent,
 * account-level notice region pinned below the sidebar nav — not a toast
 * (transient) and not a page banner (CompleteWPConnection, dashboard-only).
 *
 * This component sorts, caps and renders; it does not fetch or decide
 * whether any given alert should exist — that's each producer hook's job
 * (see useQuotaViewsAlert.js and friends).
 *
 * @typedef {Object} SidebarAlertAction
 * @property {string} label
 * @property {string=} to - internal route, rendered as a <Link>
 * @property {function=} onClick - for actions that aren't navigation (e.g. resend email)
 *
 * @typedef {Object} SidebarAlert
 * @property {string} id - stable; the dismissal key
 * @property {"critical"|"warning"|"info"|"promo"} variant
 * @property {string} icon - Icon name from theme/untitled/icons
 * @property {string=} title - required unless variant === "promo"
 * @property {string} body - one sentence, ideally <= 90 chars
 * @property {SidebarAlertAction=} action - at most one
 * @property {boolean=} dismissible - ignored (forced false) on critical/warning
 */
import Icon from "../../../theme/untitled/icons/Icon";
import { Link } from "react-router-dom";
import "./SidebarAlerts.css";

const VARIANT_ORDER = { critical: 0, warning: 1, info: 2, promo: 3 };

/**
 * Sort critical → warning → info → promo, drop every promo but the first,
 * and drop it too if a critical is present, then cap at two. Alerts are
 * assumed to already be in "newest first" order within their own variant —
 * these are synthetic (no created_at), so producer call order in
 * SidebarMenu.js stands in for recency.
 *
 * @param {SidebarAlert[]} alerts
 * @returns {SidebarAlert[]}
 */
function selectAlerts(alerts) {
  const sorted = [...alerts].sort(
    (a, b) => VARIANT_ORDER[a.variant] - VARIANT_ORDER[b.variant]
  );

  const hasCritical = sorted.some((a) => a.variant === "critical");
  let promoUsed = false;

  const filtered = sorted.filter((alert) => {
    if (alert.variant !== "promo") return true;
    if (hasCritical || promoUsed) return false;
    promoUsed = true;
    return true;
  });

  return filtered.slice(0, 2);
}

const Alert = ({ alert, onDismiss }) => {
  // Dismissal is forced off for critical/warning regardless of what the
  // producer sets — the state persists, so hiding it only hides the fix.
  const dismissible =
    alert.dismissible && !["critical", "warning"].includes(alert.variant);

  return (
    <div
      className={`sba-alert sba-alert--${alert.variant}`}
      role={
        alert.variant === "critical" || alert.variant === "warning"
          ? "status"
          : undefined
      }
    >
      <span className="sba-icon">
        <Icon name={alert.icon} size={16} />
      </span>
      <div className="sba-body">
        {alert.title && (
          <b className={dismissible ? "sba-pad-dismiss" : undefined}>
            {alert.title}
          </b>
        )}
        <p className={!alert.title && dismissible ? "sba-pad-dismiss" : undefined}>
          {alert.body}
        </p>
        {alert.action &&
          (alert.action.to ? (
            <Link to={alert.action.to} className="sba-action">
              {alert.action.label}
              <Icon name="ArrowRight" size={14} />
            </Link>
          ) : (
            <button
              type="button"
              className="sba-action sba-action--button"
              onClick={alert.action.onClick}
            >
              {alert.action.label}
              <Icon name="ArrowRight" size={14} />
            </button>
          ))}
      </div>
      {dismissible && (
        <button
          type="button"
          className="sba-dismiss"
          aria-label="Dismiss"
          onClick={() => onDismiss(alert.id)}
        >
          <Icon name="XClose" size={12} />
        </button>
      )}
    </div>
  );
};

/**
 * @param {Object} props
 * @param {SidebarAlert[]} props.alerts
 * @param {function} props.onDismiss - (id: string) => void
 */
const SidebarAlerts = ({ alerts, onDismiss }) => {
  const visible = selectAlerts(alerts);

  if (!visible.length) {
    return null;
  }

  return (
    <div className="sba-region" role="region" aria-label="Notices">
      {visible.map((alert) => (
        <Alert key={alert.id} alert={alert} onDismiss={onDismiss} />
      ))}
    </div>
  );
};

export default SidebarAlerts;
